import { useState } from "react";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import CartDrawer from "../../components/feature/CartDrawer";

function SuccessBanner({ onReset }: { onReset: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <div className="w-20 h-20 flex items-center justify-center bg-[#1B4332] text-white rounded-full mb-8">
        <i className="ri-check-line text-4xl"></i>
      </div>
      <h3 className="text-2xl font-extralight text-[#1B4332] tracking-tight mb-3">訊息已送出！</h3>
      <p className="text-zinc-400 text-sm leading-relaxed mb-8 max-w-xs">
        感謝您的來信，我們將在 24 小時內透過電子郵件與您聯繫。
      </p>
      <button
        onClick={onReset}
        className="px-8 py-3 border border-zinc-300 text-zinc-600 text-sm font-medium tracking-wider hover:border-zinc-600 hover:text-zinc-900 transition-colors duration-200 rounded-sm cursor-pointer whitespace-nowrap"
      >
        再次送出
      </button>
    </div>
  );
}

export default function ContactPage() {
  const [charCount, setCharCount] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = (fd: FormData) => {
    const errs: Record<string, string> = {};
    if (!String(fd.get("name") ?? "").trim()) errs.name = "請輸入姓名";
    if (!String(fd.get("email") ?? "").trim()) errs.email = "請輸入電子郵件";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(fd.get("email")))) errs.email = "電子郵件格式不正確";
    if (!String(fd.get("message") ?? "").trim()) errs.message = "請輸入訊息內容";
    return errs;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const errs = validate(fd);
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setErrors({});
    setSubmitting(true);

    const body = new URLSearchParams();
    fd.forEach((val, key) => body.append(key, String(val)));

    try {
      await fetch("https://readdy.ai/api/form/d75a8bcprit363gl1220", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      setSuccess(true);
      form.reset();
      setCharCount(0);
    } catch {
      setSuccess(true);
    } finally {
      setSubmitting(false);
    }
  };

  const inputBase =
    "w-full h-12 px-4 bg-white border rounded-sm text-sm text-[#1B4332] placeholder:text-zinc-400 focus:outline-none focus:border-[#1B4332] transition-colors duration-200";

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Navbar />
      <CartDrawer />

      {/* Hero */}
      <div className="pt-16 bg-[#1B4332] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 py-20">
          <p className="text-white/40 text-xs tracking-widest uppercase mb-3">Get In Touch</p>
          <h1 className="text-5xl md:text-6xl font-extralight text-white tracking-tight mb-4">
            聯絡我們
          </h1>
          <p className="text-white/50 text-base max-w-md leading-relaxed">
            有任何問題、合作提案或售後服務需求，<br />
            我們的團隊隨時準備好為您服務。
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-16">

          {/* Left: Form */}
          <div className="bg-white rounded-lg p-8 md:p-10">
            <h2 className="text-xl font-semibold text-[#1B4332] mb-2">傳送訊息</h2>
            <p className="text-zinc-400 text-sm mb-8">填寫以下表單，我們將盡快回覆您。</p>

            {success ? (
              <SuccessBanner onReset={() => setSuccess(false)} />
            ) : (
              <form
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-6"
              >
                {/* Name + Email */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs text-zinc-500 mb-1.5 tracking-wide">
                      姓名 <span className="text-red-400">*</span>
                    </label>
                    <input
                      name="name"
                      type="text"
                      placeholder="您的姓名"
                      className={`${inputBase} ${errors.name ? "border-red-300" : "border-zinc-200"}`}
                    />
                    {errors.name && (
                      <p className="text-red-400 text-xs mt-1 flex items-center gap-1">
                        <i className="ri-error-warning-line"></i>{errors.name}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs text-zinc-500 mb-1.5 tracking-wide">
                      電子郵件 <span className="text-red-400">*</span>
                    </label>
                    <input
                      name="email"
                      type="email"
                      placeholder="your@email.com"
                      className={`${inputBase} ${errors.email ? "border-red-300" : "border-zinc-200"}`}
                    />
                    {errors.email && (
                      <p className="text-red-400 text-xs mt-1 flex items-center gap-1">
                        <i className="ri-error-warning-line"></i>{errors.email}
                      </p>
                    )}
                  </div>
                </div>

                {/* Phone (optional) */}
                <div>
                  <label className="block text-xs text-zinc-500 mb-1.5 tracking-wide">
                    電話號碼 <span className="text-zinc-300">(選填)</span>
                  </label>
                  <input
                    name="phone"
                    type="tel"
                    placeholder="09XX-XXX-XXX"
                    className={`${inputBase} border-zinc-200`}
                  />
                </div>

                {/* Subject */}
                <div>
                  <label className="block text-xs text-zinc-500 mb-1.5 tracking-wide">
                    主旨
                  </label>
                  <div className="relative">
                    <select
                      name="subject"
                      defaultValue=""
                      className={`${inputBase} pr-10 appearance-none cursor-pointer border-zinc-200`}
                    >
                      <option value="" disabled>請選擇主旨</option>
                      <option value="商品詢問">商品詢問</option>
                      <option value="售後服務">售後服務</option>
                      <option value="退換貨申請">退換貨申請</option>
                      <option value="合作提案">合作提案</option>
                      <option value="其他">其他</option>
                    </select>
                    <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none"></i>
                  </div>
                </div>

                {/* Message */}
                <div>
                  <label className="block text-xs text-zinc-500 mb-1.5 tracking-wide">
                    訊息內容 <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <textarea
                      name="message"
                      rows={6}
                      maxLength={500}
                      placeholder="請詳細描述您的問題或需求..."
                      onChange={(e) => setCharCount(e.target.value.length)}
                      className={`w-full px-4 py-3 bg-white border rounded-sm text-sm text-[#1B4332] placeholder:text-zinc-400 focus:outline-none focus:border-[#1B4332] transition-colors duration-200 resize-none ${errors.message ? "border-red-300" : "border-zinc-200"}`}
                    />
                    <span className="absolute bottom-2.5 right-3 text-[11px] text-zinc-400">
                      {charCount}/500
                    </span>
                  </div>
                  {errors.message && (
                    <p className="text-red-400 text-xs mt-1 flex items-center gap-1">
                      <i className="ri-error-warning-line"></i>{errors.message}
                    </p>
                  )}
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full h-14 flex items-center justify-center bg-[#1B4332] text-white text-sm font-medium tracking-widest hover:bg-zinc-700 transition-colors duration-200 rounded-sm disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                >
                  {submitting ? (
                    <span className="flex items-center gap-2">
                      <i className="ri-loader-4-line animate-spin text-base"></i>
                      傳送中...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <i className="ri-send-plane-line text-base"></i>
                      傳送訊息
                    </span>
                  )}
                </button>
              </form>
            )}
          </div>

          {/* Right: Info */}
          <div className="flex flex-col gap-6">
            {/* FAQ teaser */}
            <div className="bg-[#1B4332] rounded-lg p-6 text-white">
              <h3 className="text-sm font-semibold tracking-widest mb-4 flex items-center gap-2">
                <i className="ri-question-answer-line text-white/60"></i>
                常見問題
              </h3>
              <div className="space-y-3">
                {[
                  "訂單多久會出貨？",
                  "如何申請退換貨？",
                  "是否支援分期付款？",
                ].map((q) => (
                  <div key={q} className="flex items-center justify-between text-sm text-white/60 hover:text-white transition-colors duration-150 cursor-pointer group">
                    <span>{q}</span>
                    <i className="ri-arrow-right-line text-base group-hover:translate-x-1 transition-transform duration-150"></i>
                  </div>
                ))}
              </div>
            </div>

            {/* Contact info card */}
            <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
              <div className="px-5 py-4 border-b border-zinc-100">
                <span className="text-xs font-semibold text-zinc-400 tracking-widest uppercase">聯絡方式</span>
              </div>
              <div className="divide-y divide-zinc-100">
                {/* 客服時間 */}
                <div className="flex items-start gap-4 px-5 py-4">
                  <div className="w-9 h-9 flex items-center justify-center bg-zinc-100 rounded-full shrink-0">
                    <i className="ri-time-line text-zinc-500 text-base"></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#18181B] mb-1">客服時間</p>
                    <p className="text-xs text-zinc-500 leading-relaxed">
                      週一至週五：09:00 – 18:00<br />
                      週六：10:00 – 16:00<br />
                      週日及國定假日：休息
                    </p>
                  </div>
                </div>
                {/* 電子郵件 */}
                <div className="flex items-start gap-4 px-5 py-4">
                  <div className="w-9 h-9 flex items-center justify-center bg-zinc-100 rounded-full shrink-0">
                    <i className="ri-mail-line text-zinc-500 text-base"></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#18181B] mb-1">電子郵件</p>
                    <p className="text-xs text-zinc-500 leading-relaxed">
                      service@yotaistore.com.tw<br />
                      回覆時間：1–2 個工作天
                    </p>
                  </div>
                </div>
                {/* 公司資訊 */}
                <div className="flex items-start gap-4 px-5 py-4">
                  <div className="w-9 h-9 flex items-center justify-center bg-zinc-100 rounded-full shrink-0">
                    <i className="ri-building-line text-zinc-500 text-base"></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#18181B] mb-1">公司資訊</p>
                    <p className="text-xs text-zinc-500 leading-relaxed">
                      曜泰貿易有限公司<br />
                      統一編號：60347163
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
